import { type JSX, type ReactNode, useEffect, useRef, useState } from 'react';

import { Schliesskreuz } from './Schliesskreuz';
/* Unter dieser Breite wird die gewählte Seitenleiste zum unteren Blatt (Auftrag CRM-105). */
import { TELEFON } from './telefon';

/** Unter dieser Breite steht die Seitenleiste unter der Fläche (stil.css, `.planung__rumpf`). */
const SCHMAL = '(max-width: 900px)';
/** So lange nach einem Tippen in der Fläche gilt eine neue Seitenleiste als gewählt. */
const WAHLFRIST_MS = 1500;

/**
 * DER SEITENTYP PLANUNG (ÄA-41, Entwurf v2) — Empfang, Termine,
 * Trainingsplanung, Besetzung, Kalender und Honorare.
 *
 * Oben die WERKZEUGLEISTE auf dem Grund (Datum, Standort, Ansicht, Suche),
 * darunter die ARBEITSFLÄCHE in voller Breite und rechts die SEITENLEISTE für
 * das, was gerade gewählt ist. Ohne Seitenleiste nimmt die Arbeitsfläche die
 * ganze Breite — die Zeitachse der Trainingsplanung braucht jede Handbreit
 * (TAG-03).
 *
 * Konflikte und Fristen stehen in der Achtungsfarbe (`marke--warn`,
 * `streifen--warn`) — der Baustein färbt nichts selbst.
 */
export function Planung({
  werkzeuge,
  seite,
  seitenBeschriftung = 'Auswahl',
  seiteSchwebend = false,
  oben,
  children,
}: {
  /** Die Werkzeugleiste; fehlt sie, beginnt die Seite mit der Fläche. */
  readonly werkzeuge?: ReactNode;
  /** Die Seitenleiste für das Gewählte. */
  readonly seite?: ReactNode;
  /** Ihr Name für Vorlesehilfen. */
  readonly seitenBeschriftung?: string;
  /**
   * DIE SEITENLEISTE SCHWEBT, statt Breite zu nehmen. Für Flächen, auf denen
   * gezogen wird (die Zeitachse der Trainingsplanung): Erschiene die Leiste
   * erst beim Anfassen als Spalte, würde die Fläche mitten im Ziehen
   * schmaler und jedes Ziel verrutschte.
   */
  readonly seiteSchwebend?: boolean;
  /** Meldungen und Hinweise zwischen Werkzeugleiste und Fläche. */
  readonly oben?: ReactNode;
  readonly children: ReactNode;
}): JSX.Element {
  const flaecheRef = useRef<HTMLDivElement>(null);
  const seiteRef = useRef<HTMLElement>(null);
  const [blatt, setzeBlatt] = useState(false);
  const hatSeite = seite !== undefined;

  /*
   * DAS GEWÄHLTE ZEIGT SICH AUCH AUF DEM SCHMALEN SCHIRM (Auftrag CRM-105).
   * Unter 900 px steht die Seitenleiste unter der langen Fläche: Ein Tippen
   * auf einen Termin füllte sie, aber niemand sah es. Wer gewählt hat,
   * erkennt der Baustein ohne Zutun der Masken: Ändert sich die Leiste kurz
   * nach einem Tippen in der Fläche, ist das die Antwort darauf. Dann rollt
   * sie zwischen 600 und 900 px ins Bild, darunter erscheint sie als unteres
   * Blatt mit Kreuz. Auf breiterem Schirm geschieht nichts.
   */
  useEffect(() => {
    const flaeche = flaecheRef.current;
    const leiste = seiteRef.current;
    if (flaeche === null || leiste === null || typeof window.matchMedia !== 'function') return;
    let getippt = 0;
    const zeigen = (): void => {
      if (window.matchMedia(TELEFON).matches) setzeBlatt(true);
      else if (window.matchMedia(SCHMAL).matches && !seiteSchwebend) {
        leiste.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    };
    const beiTippen = (): void => {
      getippt = Date.now();
    };
    const beobachter = new MutationObserver(() => {
      if (Date.now() - getippt > WAHLFRIST_MS) return;
      getippt = 0;
      zeigen();
    });
    beobachter.observe(leiste, { childList: true, subtree: true, characterData: true });
    flaeche.addEventListener('pointerdown', beiTippen);
    flaeche.addEventListener('keydown', beiTippen);
    /* Die schwebende Leiste entsteht erst mit der Wahl — ihr Erscheinen ist die Wahl. */
    if (seiteSchwebend) zeigen();
    return () => {
      beobachter.disconnect();
      flaeche.removeEventListener('pointerdown', beiTippen);
      flaeche.removeEventListener('keydown', beiTippen);
    };
  }, [hatSeite, seiteSchwebend]);

  /* Ohne Seitenleiste gibt es kein Blatt; die nächste Wahl öffnet es neu. */
  useEffect(() => {
    if (!hatSeite) setzeBlatt(false);
  }, [hatSeite]);

  const leiste = (schwebend: boolean): JSX.Element => (
    <aside
      ref={seiteRef}
      className={[
        'karte planung__seite',
        schwebend ? 'planung__seite--schwebend' : '',
        blatt ? 'planung__seite--blatt' : '',
      ].filter((k) => k !== '').join(' ')}
      aria-label={seitenBeschriftung}
    >
      {blatt && (
        <div className="planung__blattkopf">
          <Schliesskreuz beschriftung={`${seitenBeschriftung} schließen`} beiKlick={() => setzeBlatt(false)} />
        </div>
      )}
      {seite}
    </aside>
  );

  return (
    <div className="planung">
      {werkzeuge !== undefined && <div className="werkzeugleiste">{werkzeuge}</div>}
      {oben !== undefined && <div className="planung__oben">{oben}</div>}
      {seite === undefined || seiteSchwebend ? (
        <>
          <div className="planung__flaeche" ref={flaecheRef}>{children}</div>
          {seite !== undefined && leiste(true)}
        </>
      ) : (
        <div className="planung__rumpf">
          <div className="planung__flaeche" ref={flaecheRef}>{children}</div>
          {leiste(false)}
        </div>
      )}
    </div>
  );
}
